import { useEffect, useRef, type RefObject } from 'react';

type VerticalPhase = 'pause-top' | 'pan-down' | 'pause-bottom' | 'pan-up';

export interface UseBracketKioskCarouselOptions {
  enabled: boolean;
  segmentCount: number;
  activeSegmentIndex: number;
  onSegmentChange: (nextIndex: number) => void;
  containerRef: RefObject<HTMLElement | null>;
  segmentRef: RefObject<HTMLElement | null>;
  pauseMs?: number;
  pixelsPerSecond?: number;
  minPanMs?: number;
  verticalCyclesPerSegment?: number;
}

/**
 * Vertical-first kiosk carousel: completes pause → pan down → pause → pan up (× cycles)
 * on the active segment, then advances to the next segment.
 */
export function useBracketKioskCarousel({
  enabled,
  segmentCount,
  activeSegmentIndex,
  onSegmentChange,
  containerRef,
  segmentRef,
  pauseMs = 3000,
  pixelsPerSecond = 36,
  minPanMs = 8000,
  verticalCyclesPerSegment = 1,
}: UseBracketKioskCarouselOptions): void {
  const animationStateRef = useRef({
    phase: 'pause-top' as VerticalPhase,
    phaseStart: 0,
    cyclesCompleted: 0,
  });

  useEffect(() => {
    animationStateRef.current.phase = 'pause-top';
    animationStateRef.current.phaseStart = window.performance.now();
    animationStateRef.current.cyclesCompleted = 0;

    const segment = segmentRef.current;
    if (segment) {
      segment.style.transform = 'translate3d(0, 0, 0)';
    }
  }, [activeSegmentIndex, segmentRef]);

  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!enabled || prefersReducedMotion || segmentCount <= 0) {
      const segment = segmentRef.current;
      if (segment) {
        segment.style.transform = '';
      }
      return undefined;
    }

    let frameId = 0;
    const animationState = animationStateRef.current;
    if (animationState.phaseStart === 0) {
      animationState.phaseStart = window.performance.now();
    }

    let maxY = 0;
    let panDurationMs = minPanMs;

    const measure = () => {
      const container = containerRef.current;
      const segment = segmentRef.current;
      if (!container || !segment) return;

      maxY = Math.max(0, segment.scrollHeight - container.clientHeight);
      panDurationMs = maxY > 0 ? Math.max(minPanMs, (maxY / pixelsPerSecond) * 1000) : 0;
    };

    const applyOffset = (y: number) => {
      const segment = segmentRef.current;
      if (!segment) return;
      segment.style.transform = `translate3d(0, ${-y}px, 0)`;
    };

    const advanceSegment = () => {
      animationState.phase = 'pause-top';
      animationState.phaseStart = window.performance.now();
      animationState.cyclesCompleted = 0;
      applyOffset(0);
      onSegmentChange((activeSegmentIndex + 1) % segmentCount);
    };

    const tick = (now: number) => {
      measure();

      const elapsed = now - animationState.phaseStart;

      if (maxY === 0) {
        applyOffset(0);
        if (animationState.phase === 'pause-top' && elapsed >= pauseMs) {
          advanceSegment();
        }
        frameId = requestAnimationFrame(tick);
        return;
      }

      switch (animationState.phase) {
        case 'pause-top':
          applyOffset(0);
          if (elapsed >= pauseMs) {
            animationState.phase = 'pan-down';
            animationState.phaseStart = now;
          }
          break;
        case 'pan-down': {
          const progress = Math.min(1, elapsed / panDurationMs);
          applyOffset(maxY * progress);
          if (progress >= 1) {
            animationState.phase = 'pause-bottom';
            animationState.phaseStart = now;
          }
          break;
        }
        case 'pause-bottom':
          applyOffset(maxY);
          if (elapsed >= pauseMs) {
            animationState.phase = 'pan-up';
            animationState.phaseStart = now;
          }
          break;
        case 'pan-up': {
          const progress = Math.min(1, elapsed / panDurationMs);
          applyOffset(maxY * (1 - progress));
          if (progress >= 1) {
            animationState.cyclesCompleted += 1;
            if (animationState.cyclesCompleted >= verticalCyclesPerSegment) {
              advanceSegment();
            } else {
              animationState.phase = 'pause-top';
              animationState.phaseStart = now;
            }
          }
          break;
        }
        default:
          break;
      }

      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);

    let observer: InstanceType<typeof window.ResizeObserver> | undefined;
    if (typeof window.ResizeObserver !== 'undefined') {
      observer = new window.ResizeObserver(() => measure());
      if (containerRef.current) observer.observe(containerRef.current);
      if (segmentRef.current) observer.observe(segmentRef.current);
    }

    return () => {
      cancelAnimationFrame(frameId);
      observer?.disconnect();
    };
  }, [
    activeSegmentIndex,
    containerRef,
    enabled,
    minPanMs,
    onSegmentChange,
    pauseMs,
    pixelsPerSecond,
    segmentCount,
    segmentRef,
    verticalCyclesPerSegment,
  ]);
}
